import { Drawer, Form, Input, Checkbox, Select, Button, Space, Divider, message } from "antd";
import { useEffect, useState } from "react";
import { addStore } from "../../services/storeserivce";
import { AddStoreDto } from "../../types/storetypes";
import { LanguageDto } from "../../types/languagetypes";
import { Currency } from "../../types/currencytypes";
import { getActiveLanguages } from "../../services/languageservice";
import { getActiveCurrencies } from "../../services/currencyservice";
import CancelButton from "../common/cancelbutton";
import FormButton from "../common/formbutton";
import { PlusOutlined } from "@ant-design/icons";

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    currentUserGuid: string;
}

const AddStoreDrawer: React.FC<Props> = ({
    open,
    onClose,
    onSuccess,
    currentUserGuid
}) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);

    useEffect(() => {
        if (!open) return;

        const loadRefs = async () => {
            const langs = await getActiveLanguages();
            const currs = await getActiveCurrencies();
            setLanguages(langs.data);
            setCurrencies(currs);
        };

        loadRefs();
    }, [open]);

    const supportedLanguages =
        Form.useWatch("supportedLanguageGuids", form) || [];

    useEffect(() => {
        const primary = form.getFieldValue("primaryLanguageGuid");

        if (
            (!supportedLanguages.length && primary) ||
            (primary && !supportedLanguages.includes(primary))
        ) {
            form.setFieldsValue({ primaryLanguageGuid: null });
        }
    }, [supportedLanguages, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            const kitchenStations: string[] = values.kitchenStations || [];

            const dto: AddStoreDto = {
                storeNumber: values.storeNumber,
                storeDisplayName: values.storeDisplayName,
                displayPhoneNumber: values.displayPhoneNumber,

                buildingName: values.buildingName,
                buildingAddress1: values.buildingAddress1,
                buildingAddress2: values.buildingAddress2,
                buildingCity: values.buildingCity,
                buildingStateCode: values.buildingStateCode,
                buildingCounty: values.buildingCounty,
                buildingZip: values.buildingZip,

                //legal entity fields
                legalEntityName: values.legalEntityName,
                legalEntityAddress1: values.legalEntityAddress1,
                legalEntityAddress2: values.legalEntityAddress2,
                legalEntityCity: values.legalEntityCity,
                legalEntityZip: values.legalEntityZip,
                legalEntityCountry: values.legalEntityCountry,
                legalEntityRegistryCode: values.legalEntityRegistryCode,
                legalEntityVatNumber: values.legalEntityVatNumber,

                sub_Back: kitchenStations.includes("Sub_Back"),
                fryer: kitchenStations.includes("Fryer"),
                drinks: kitchenStations.includes("Drinks"),
                expo: kitchenStations.includes("Expo"),
                finish_Front: kitchenStations.includes("Finish_Front"),
                finish_Expo: kitchenStations.includes("Finish_Expo"),

                supportedLanguageGuids: values.supportedLanguageGuids,
                primaryLanguageGuid: values.primaryLanguageGuid,
                currencyGuid: values.currencyGuid,

                createdBy: currentUserGuid
            };

            await addStore(dto);

            message.success("Store added successfully");
            form.resetFields();
            onSuccess();
        } catch (err: any) {
            if (err?.errorFields) return;

            message.error(
                err?.response?.data ??
                err?.message ??
                "Failed to add store"
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            title="Add New Store"
            open={open}
            onClose={onClose}
            destroyOnClose
            width={500}
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px',
                },
            }}
        >
            <Form layout="vertical" form={form}>

                <Divider>Store Identity</Divider>

                <Form.Item name="storeNumber" label="Store #" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="storeDisplayName" label="Store Name" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="displayPhoneNumber" label="Phone Number"
                    rules={[
                        { required: true },
                        {
                            pattern: /\d+/,
                            message: "Phone number must contain digits"
                        }]
                    }>
                    <Input />
                </Form.Item>

                <Divider>Address</Divider>

                <Form.Item name="buildingName" label="Building Name" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="buildingAddress1" label="Address Line 1" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="buildingAddress2" label="Address Line 2">
                    <Input />
                </Form.Item>

                <Form.Item name="buildingCity" label="City" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="buildingStateCode" label="State">
                    <Input />
                </Form.Item>

                <Form.Item name="buildingCounty" label="County">
                    <Input />
                </Form.Item>

                <Form.Item name="buildingZip" label="Zip"
                    rules={[
                        { required: true },
                        {
                            pattern: /\d+/,
                            message: "Zip must include a number"
                        }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Divider>Legal Details</Divider>
                <Form.Item name="legalEntityName" label="Legal Entity Name" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="legalEntityAddress1" label="Address Line 1" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="legalEntityAddress2" label="Address Line 2">
                    <Input />
                </Form.Item>

                <Form.Item name="legalEntityCity" label="City" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="legalEntityZip" label="Zip"
                    rules={[
                        { required: true },
                        { pattern: /^\d{4,10}$/, message: "Enter valid zip" }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item name="legalEntityCountry" label="Country" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="legalEntityRegistryCode" label="Registry Code"
                    rules={[
                        { required: true },
                        {
                            pattern: /\d+/,
                            message: "Registry code must include a number"
                        }
                    ]}>
                    <Input />
                </Form.Item>

                <Form.Item name="legalEntityVatNumber" label="VAT Number" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Divider>Kitchen Stations</Divider>

                <Form.Item
                    name="kitchenStations"
                    rules={[
                        {
                            validator: (_, value: string[]) =>
                                value && value.length > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error("Select at least one kitchen station")),
                        },
                    ]}
                >
                    <Checkbox.Group>
                        <Checkbox value="Sub_Back">Subs</Checkbox>
                        <Checkbox value="Fryer">Fryer</Checkbox>
                        <Checkbox value="Drinks">Drinks</Checkbox>
                        <Checkbox value="Expo">Expo</Checkbox>
                        <Checkbox value="Finish_Front">Finish Front</Checkbox>
                        <Checkbox value="Finish_Expo">Finish Expo</Checkbox>
                    </Checkbox.Group>
                </Form.Item>
                <Divider>Languages</Divider>

                <Form.Item
                    label="Supported Languages"
                    name="supportedLanguageGuids"
                    required
                    rules={[
                        {
                            validator: (_, value) => {
                                if (!value || value.length === 0) {
                                    return Promise.reject(
                                        new Error("Select at least one supported language")
                                    );
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder="Select supported languages"
                        options={languages.map((l) => ({
                            label: `${l.name} (${l.iso639_1})`,
                            value: l.guid,
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Primary Language"
                    required
                    dependencies={["supportedLanguageGuids"]}
                >
                    {({ getFieldValue }) => {
                        const supported: string[] =
                            getFieldValue("supportedLanguageGuids") || [];

                        const options = languages
                            .filter(l => supported.includes(l.guid))
                            .map(l => ({
                                label: `${l.name} (${l.iso639_1})`,
                                value: l.guid,
                            }));

                        return (
                            <Form.Item
                                name="primaryLanguageGuid"
                                noStyle
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (supported.length > 0 && !value) {
                                                return Promise.reject(
                                                    new Error("Primary language is required")
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Select
                                    allowClear
                                    disabled={supported.length === 0}
                                    placeholder={
                                        supported.length === 0
                                            ? "Select supported languages first"
                                            : "Select primary language"
                                    }
                                    options={options}
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>

                <Divider>Currency</Divider>

                <Form.Item
                    label="Currency"
                    name="currencyGuid"
                    rules={[
                        { required: true, message: "Please select a currency" }
                    ]}
                >
                    <Select
                        placeholder="Select currency"
                        options={currencies.map(c => ({
                            label: `${c.currencyName} (${c.currencyCode})`,
                            value: c.guid
                        }))}
                    />
                </Form.Item>
            </Form>

            <div style={{ textAlign: "right" }}>
                <Space>
                    <CancelButton onClick={onClose} />
                    <FormButton
                        title={"Add Store"}
                        icon={<PlusOutlined />}
                        onClick={handleSubmit}
                        loading={saving}
                    />
                </Space>
            </div>
        </Drawer>
    );
};

export default AddStoreDrawer;